<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Analise do Pipeline',
  },
});

const activeView = ref('funnel');

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}K`;
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

const byFunnel = computed(() => props.data?.by_funnel || []);
const byStage = computed(() => props.data?.by_stage || []);
const byMonth = computed(() => props.data?.by_expected_close || []);

const maxFunnelValue = computed(() => {
  return Math.max(...byFunnel.value.map(f => f.total_value || 0), 1);
});

const maxStageValue = computed(() => {
  return Math.max(...byStage.value.map(s => s.total_value || 0), 1);
});
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-bar-chart-2 text-lg text-n-teal-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex gap-1">
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'funnel'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'funnel'"
        >
          Por Funil
        </button>
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'stage'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'stage'"
        >
          Por Etapa
        </button>
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'month'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'month'"
        >
          Por Mes
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- By Funnel View -->
    <div v-else-if="activeView === 'funnel'" class="flex flex-col gap-3">
      <div
        v-if="byFunnel.length === 0"
        class="text-center text-n-slate-11 py-8"
      >
        Sem dados de pipeline
      </div>
      <div
        v-for="funnel in byFunnel"
        :key="funnel.funnel_id"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">{{
            funnel.funnel_name
          }}</span>
          <div class="flex items-center gap-2">
            <span class="text-xs text-n-slate-11">{{ formatNumber(funnel.deals_count) }} negocios</span>
            <span class="text-sm font-bold text-n-teal-11">{{
              formatCurrency(funnel.total_value)
            }}</span>
          </div>
        </div>
        <div class="h-4 bg-n-alpha-2 rounded-full overflow-hidden">
          <div
            class="h-full bg-gradient-to-r from-n-blue-9 to-n-teal-9 rounded-full transition-all duration-300"
            :style="{
              width: `${(funnel.total_value / maxFunnelValue) * 100}%`,
            }"
          />
        </div>
        <div class="flex justify-between text-xs text-n-slate-11">
          <span>Ponderado: {{ formatCurrency(funnel.weighted_value) }}</span>
          <span>Medio: {{ formatCurrency(funnel.avg_value) }}</span>
        </div>
      </div>
    </div>

    <!-- By Stage View -->
    <div v-else-if="activeView === 'stage'" class="flex flex-col gap-3">
      <div v-if="byStage.length === 0" class="text-center text-n-slate-11 py-8">
        Sem dados de etapas
      </div>
      <div
        v-for="stage in byStage"
        :key="stage.stage_id"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span
              class="w-3 h-3 rounded"
              :style="{ backgroundColor: stage.stage_color || '#6366f1' }"
            />
            <span class="text-sm font-medium text-n-slate-12">{{
              stage.stage_name
            }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-n-slate-11">{{
              formatNumber(stage.deals_count)
            }}</span>
            <span class="text-sm font-bold text-n-teal-11">{{
              formatCurrency(stage.total_value)
            }}</span>
          </div>
        </div>
        <div class="h-3 bg-n-alpha-2 rounded-full overflow-hidden">
          <div
            class="h-full rounded-full transition-all duration-300"
            :style="{
              width: `${(stage.total_value / maxStageValue) * 100}%`,
              backgroundColor: stage.stage_color || '#6366f1',
            }"
          />
        </div>
      </div>
    </div>

    <!-- By Month View -->
    <div v-else-if="activeView === 'month'" class="flex flex-col gap-3">
      <div v-if="byMonth.length === 0" class="text-center text-n-slate-11 py-8">
        Sem previsao de fechamento
      </div>
      <div
        v-for="month in byMonth"
        :key="month.month"
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">{{
            month.month_name
          }}</span>
          <span class="text-xs text-n-slate-11">{{ formatNumber(month.deals_count) }} negocios</span>
        </div>
        <div class="flex flex-col items-end">
          <span class="text-lg font-bold text-n-teal-11">{{
            formatCurrency(month.total_value)
          }}</span>
          <span class="text-xs text-n-slate-11">Ponderado: {{ formatCurrency(month.weighted_value) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
